import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import Papa from 'papaparse';
import { parse, isValid, format } from 'date-fns';
import { ref, onValue } from 'firebase/database';
import { auth, database } from '../firebase/firebaseConfig';
import {
  saveReconciliationSettings,
  upsertReconciliationTransactions,
  markReconciliationTransaction,
  markTransactionReconciliationMatch,
  markTransactionPaid,
} from '../firebase/firebaseUtils';
import {
  Card,
  CardContent,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  ModalContainer,
  ModalContent,
  Textarea,
  ToggleWrapper,
  ToggleInput,
  ToggleSlider,
  Dropdown,
  DropdownContent,
} from './StyledComponents';

const DATE_FORMATS = [
  'dd/MM/yyyy',
  'd/M/yyyy',
  'dd/MM/yy',
  'd/M/yy',
  'yyyy-MM-dd',
  'yyyy/MM/dd',
  'MM/dd/yyyy',
  'M/d/yyyy',
  'MM/dd/yy',
  'M/d/yy',
];

const ALLOWED_ACCOUNT_TYPES = new Set(['Bank', 'Loan']);

const getReconciliationMatches = (transaction) => (
  transaction?.reconciliationMatches && typeof transaction.reconciliationMatches === 'object'
    ? transaction.reconciliationMatches
    : {}
);

const hasReconciliationMatch = (transaction, accountId) => {
  if (!accountId) return false;
  const match = getReconciliationMatches(transaction)[accountId];
  return Boolean(match?.reconciliationTransactionId || match?.reconciledAt || match === true);
};

const getSelectedTransferAccountIds = (transaction, selectedAccountSet) => {
  if (!transaction?.fromAccount || !transaction?.toAccount) return [];
  if (transaction.fromAccount === transaction.toAccount) return [];
  return [transaction.fromAccount, transaction.toAccount].filter((accountId) => (
    selectedAccountSet.has(accountId)
  ));
};

const canReconcilePaidTransferSide = (transaction, recon, selectedAccountSet) => {
  if (!transaction?.isPaid || !recon?.accountId) return false;
  const transferAccountIds = getSelectedTransferAccountIds(transaction, selectedAccountSet);
  return (
    transferAccountIds.length >= 2 &&
    transferAccountIds.includes(recon.accountId) &&
    !hasReconciliationMatch(transaction, recon.accountId)
  );
};

const isTransferAwaitingReconciliation = (transaction, selectedAccountSet) => {
  if (!transaction?.isPaid) return false;
  const transferAccountIds = getSelectedTransferAccountIds(transaction, selectedAccountSet);
  if (transferAccountIds.length < 2) return false;
  const matchedCount = transferAccountIds.filter((accountId) => (
    hasReconciliationMatch(transaction, accountId)
  )).length;
  return matchedCount > 0 && matchedCount < transferAccountIds.length;
};

const normalizeColumnKey = (key) => String(key ?? '')
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();

const COLUMN_CANDIDATES = {
  date: ['date', 'transaction date', 'posted date', 'posting date', 'date posted'],
  description: ['description', 'details', 'memo', 'payee', 'transaction', 'narrative'],
  amount: ['amount', 'transaction amount', 'amt', 'value', 'amount (usd)'],
  debit: ['debit', 'withdrawal', 'debit amount', 'withdrawals', 'outflow'],
  credit: ['credit', 'deposit', 'credit amount', 'deposits', 'inflow'],
  pending: ['status', 'state', 'pending', 'transaction status'],
};

const FLAT_CANDIDATE_KEYS = Array.from(
  new Set(
    Object.values(COLUMN_CANDIDATES)
      .flat()
      .map((value) => normalizeColumnKey(value))
  ),
);

const csvConfig = {
  header: false,
  skipEmptyLines: true,
};

const AUTO_MATCH_AMOUNT_TOLERANCE_STRICT = 0.01;
const AUTO_MATCH_AMOUNT_TOLERANCE_SOFT = 0.5;
const AUTO_MATCH_DATE_TOLERANCE_DAYS = 3;
const AMOUNT_WEIGHT = 4;
const DESCRIPTION_WEIGHT = 3;
const DATE_WEIGHT = 2;
const ACCOUNT_WEIGHT = 1;
const MAX_TOTAL_SCORE = AMOUNT_WEIGHT + DESCRIPTION_WEIGHT + DATE_WEIGHT + ACCOUNT_WEIGHT;
const AUTO_MATCH_MIN_SCORE = 5;
const compactCellStyle = {
  padding: '10px 8px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const compactCenteredCellStyle = {
  ...compactCellStyle,
  textAlign: 'center',
};

const formatDateToISO = (value) => {
  if (value instanceof Date && isValid(value)) {
    return format(value, 'yyyy-MM-dd');
  }

  const raw = typeof value === 'number' ? String(value) : (value || '').trim();
  if (!raw) return null;

  const enforceCentury = (date, source) => {
    if (!date || !isValid(date) || typeof source !== 'string') {
      return date;
    }
    if (date.getFullYear() > 100) {
      return date;
    }
    const match = source.trim().match(/(\d{1,2})[/-](\d{1,2})[/-](\d{2})(?!\d)/);
    if (!match) {
      return date;
    }
    const twoDigitYear = parseInt(match[3], 10);
    const pivot = 70; // interpret 70-99 as 1900s, otherwise 2000s
    const adjustedYear = twoDigitYear >= pivot ? 1900 + twoDigitYear : 2000 + twoDigitYear;
    const adjusted = new Date(date);
    adjusted.setFullYear(adjustedYear);
    return adjusted;
  };

  for (const pattern of DATE_FORMATS) {
    const parsed = parse(raw, pattern, new Date());
    if (isValid(parsed)) {
      const adjusted = enforceCentury(parsed, raw);
      return format(adjusted, 'yyyy-MM-dd');
    }
  }

  const parsed = new Date(raw);
  if (isValid(parsed)) {
    const adjusted = enforceCentury(parsed, raw);
    return format(adjusted, 'yyyy-MM-dd');
  }
  return null;
};

const sanitizeAmount = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const raw = (value || '').toString().trim();
  if (!raw) return null;

  let str = raw.replace(/[,\s]/g, '');
  let negative = false;
  if (/\((.*)\)/.test(str)) {
    negative = true;
    str = str.replace(/[()]/g, '');
  }
  if (str.endsWith('-')) {
    negative = true;
    str = str.slice(0, -1);
  }
  if (str.startsWith('-')) {
    negative = true;
    str = str.slice(1);
  }
  if (/dr$/i.test(str)) {
    negative = true;
    str = str.replace(/dr$/i, '');
  }
  str = str.replace(/cr$/i, '');
  str = str.replace(/[^0-9.]/g, '');
  const amount = parseFloat(str);
  if (!Number.isFinite(amount)) return null;
  return negative ? -amount : amount;
};

const pickColumnValue = (row, candidates) => {
  const normalizedEntries = Object.entries(row).map(([key, value]) => [normalizeColumnKey(key), value]);
  for (const name of candidates) {
    const target = name.toLowerCase();
    const match = normalizedEntries.find(([key]) => key === target);
    if (match) return match[1];
  }
  return null;
};

const stripPendingPrefix = (text) => {
  if (!text) return '';
  let result = text.trim();
  const pattern = /^(?:\*+\s*)?(?:pending(?:\s+(?:transaction|payment|debit|credit|charge|pos))?)[:\-\s]*/i;
  while (pattern.test(result)) {
    result = result.replace(pattern, '').trim();
  }
  return result;
};

const normalizeDescription = (text) => {
  const stripped = stripPendingPrefix(text || '');
  const lowered = stripped.toLowerCase();
  const normalised = lowered.replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
  return normalised || 'unknown';
};

const detectPending = (row, descriptionValue) => {
  const statusValue = pickColumnValue(row, COLUMN_CANDIDATES.pending);
  if (statusValue && /pending/i.test(statusValue)) {
    return true;
  }
  return /\bpending\b/i.test(descriptionValue || '');
};

const isLikelyHeaderRow = (row) => {
  if (!Array.isArray(row)) return false;
  const normalizedCells = row.map((cell) => normalizeColumnKey(String(cell ?? '')));
  const matches = normalizedCells.reduce((count, value) => {
    if (!value) return count;
    return FLAT_CANDIDATE_KEYS.some((candidate) => value.includes(candidate)) ? count + 1 : count;
  }, 0);
  if (matches >= 2) {
    const numericLike = normalizedCells.reduce((count, value, index) => {
      const original = row[index];
      if (formatDateToISO(original) || Number.isFinite(sanitizeAmount(original))) {
        return count + 1;
      }
      return count;
    }, 0);
    if (numericLike >= row.length - matches) {
      return false;
    }
    return true;
  }
  return false;
};

const inferColumnMapping = (dataRows, headerRow) => {
  const mapping = {
    date: null,
    description: null,
    amount: null,
    debit: null,
    credit: null,
    pending: null,
  };

  const columnCount = Math.max(
    headerRow ? headerRow.length : 0,
    ...dataRows.map((row) => row.length),
  );

  if (headerRow) {
    headerRow.forEach((value, index) => {
      const normalized = normalizeColumnKey(String(value ?? ''));
      if (!normalized) return;
      Object.entries(COLUMN_CANDIDATES).forEach(([key, candidates]) => {
        if (mapping[key] !== null) return;
        const matched = candidates.some(
          (candidate) => normalized.includes(normalizeColumnKey(candidate))
        );
        if (matched) {
          mapping[key] = index;
        }
      });
    });
  }

  const sampleRows = dataRows.slice(0, 25);
  const columnStats = Array.from({ length: columnCount }, () => ({
    dateScore: 0,
    amountScore: 0,
    debitScore: 0,
    creditScore: 0,
    textScore: 0,
    pendingScore: 0,
  }));

  sampleRows.forEach((row) => {
    for (let i = 0; i < columnCount; i += 1) {
      const value = row[i];
      if (value === undefined || value === null) continue;
      const stringValue = String(value).trim();
      if (!stringValue) continue;

      if (formatDateToISO(stringValue)) {
        columnStats[i].dateScore += 1;
      }

      const amountValue = sanitizeAmount(stringValue);
      if (Number.isFinite(amountValue)) {
        columnStats[i].amountScore += 1;
        if (amountValue < 0) {
          columnStats[i].debitScore += 1;
        }
        if (amountValue > 0) {
          columnStats[i].creditScore += 1;
        }
      }

      if (/[a-zA-Z]/.test(stringValue)) {
        columnStats[i].textScore += stringValue.length;
      }

      if (/pending/i.test(stringValue)) {
        columnStats[i].pendingScore += 1;
      }
    }
  });

  const isAssigned = (index) => Object.values(mapping).some((value) => value === index);

  const selectColumn = (scoreKey, options = {}) => {
    const { thresholdRatio = 0, allowReuse = false } = options;
    let bestIndex = null;
    let bestScore = 0;
    for (let i = 0; i < columnCount; i += 1) {
      if (!allowReuse && isAssigned(i)) continue;
      const score = columnStats[i][scoreKey];
      if (score > bestScore) {
        bestScore = score;
        bestIndex = i;
      }
    }
    if (bestIndex === null) return null;
    if (thresholdRatio > 0 && sampleRows.length > 0) {
      const minScore = Math.max(1, Math.ceil(sampleRows.length * thresholdRatio));
      if (columnStats[bestIndex][scoreKey] < minScore) {
        return null;
      }
    }
    return bestIndex;
  };

  if (mapping.date === null) {
    const inferredDate = selectColumn('dateScore', { thresholdRatio: 0.5 });
    if (inferredDate !== null) {
      mapping.date = inferredDate;
    }
  }

  if (mapping.amount === null) {
    const inferredAmount = selectColumn('amountScore', { thresholdRatio: 0.5 });
    if (inferredAmount !== null) {
      mapping.amount = inferredAmount;
    }
  }

  if (mapping.description === null) {
    const inferredDescription = selectColumn('textScore', {});
    if (inferredDescription !== null) {
      mapping.description = inferredDescription;
    }
  }

  if (mapping.debit === null) {
    const inferredDebit = selectColumn('debitScore', { allowReuse: false });
    if (inferredDebit !== null && inferredDebit !== mapping.amount) {
      mapping.debit = inferredDebit;
    }
  }

  if (mapping.credit === null) {
    const inferredCredit = selectColumn('creditScore', { allowReuse: false });
    if (inferredCredit !== null && inferredCredit !== mapping.amount && inferredCredit !== mapping.debit) {
      mapping.credit = inferredCredit;
    }
  }

  if (mapping.pending === null) {
    const inferredPending = selectColumn('pendingScore', {});
    if (inferredPending !== null) {
      mapping.pending = inferredPending;
    }
  }

  if (mapping.description === null) {
    for (let i = 0; i < columnCount; i += 1) {
      if (!isAssigned(i)) {
        mapping.description = i;
        break;
      }
    }
    if (mapping.description === null && columnCount > 0) {
      mapping.description = mapping.amount !== null ? mapping.amount : 0;
    }
  }

  if (mapping.amount === null) {
    for (let i = 0; i < columnCount; i += 1) {
      if (!isAssigned(i)) {
        mapping.amount = i;
        break;
      }
    }
    if (mapping.amount === null && columnCount > 0) {
      mapping.amount = mapping.description !== null ? mapping.description : 0;
    }
  }

  return mapping;
};

const buildRowObjects = (rawRows) => {
  const arrayRows = rawRows
    .map((row) => {
      if (Array.isArray(row)) return row;
      if (row && typeof row === 'object') return Object.values(row);
      if (row === null || row === undefined) return [];
      return [row];
    })
    .filter((row) => row.some((cell) => String(cell ?? '').trim() !== ''));

  if (arrayRows.length === 0) {
    return [];
  }

  let headerRow = null;
  let dataRows = arrayRows;
  if (arrayRows.length > 0 && isLikelyHeaderRow(arrayRows[0])) {
    headerRow = arrayRows[0];
    dataRows = arrayRows.slice(1);
  }

  const mapping = inferColumnMapping(dataRows, headerRow);

  return dataRows.map((row) => {
    const obj = {};
    Object.entries(mapping).forEach(([key, index]) => {
      if (index === null) return;
      const value = row[index];
      if (value !== undefined) {
        obj[key] = value;
      }
    });

    if (headerRow) {
      headerRow.forEach((headerValue, index) => {
        const normalized = normalizeColumnKey(String(headerValue ?? ''));
        const cellValue = row[index];
        if (normalized && cellValue !== undefined) {
          obj[normalized] = cellValue;
        }
      });
    }

    return obj;
  });
};

const parseRows = (rows) => {
  const parsed = [];
  const failures = [];

  rows.forEach((row, index) => {
    if (!row || typeof row !== 'object') {
      return;
    }

    const hasContent = Object.values(row).some((value) => {
      if (value === null || value === undefined) return false;
      return String(value).trim() !== '';
    });
    if (!hasContent) {
      return;
    }

    const descriptionRaw = pickColumnValue(row, COLUMN_CANDIDATES.description) || '';
    const dateRaw = pickColumnValue(row, COLUMN_CANDIDATES.date);
    let amountValue = pickColumnValue(row, COLUMN_CANDIDATES.amount);
    let amount = sanitizeAmount(amountValue);

    if (!Number.isFinite(amount)) {
      const debit = sanitizeAmount(pickColumnValue(row, COLUMN_CANDIDATES.debit));
      const credit = sanitizeAmount(pickColumnValue(row, COLUMN_CANDIDATES.credit));
      if (Number.isFinite(debit) || Number.isFinite(credit)) {
        amount = (credit || 0) - (debit || 0);
      }
    }

    const isoDate = formatDateToISO(dateRaw);
    if (!isoDate || !Number.isFinite(amount)) {
      failures.push(index + 1);
      return;
    }

    const pending = detectPending(row, descriptionRaw);
    const cleanedDescription = stripPendingPrefix(descriptionRaw);
    parsed.push({
      date: isoDate,
      amount,
      pending,
      rawDescription: descriptionRaw || '',
      description: cleanedDescription || descriptionRaw || '',
      normalizedDescription: normalizeDescription(descriptionRaw),
    });
  });

  return { parsed, failures };
};

const formatCurrency = (value) => {
  const amount = Number(value) || 0;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

const formatDisplayDate = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed) {
      for (const pattern of DATE_FORMATS) {
        const parsed = parse(trimmed, pattern, new Date());
        if (isValid(parsed)) {
          return format(parsed, 'dd/MM/yyyy');
        }
      }
      const fallbackDate = new Date(trimmed);
      if (isValid(fallbackDate)) {
        return format(fallbackDate, 'dd/MM/yyyy');
      }
    }
    return trimmed;
  }
  const dateValue = value instanceof Date ? value : new Date(value);
  return isValid(dateValue) ? format(dateValue, 'dd/MM/yyyy') : '';
};

const getSafeDate = (value) => {
  if (!value && value !== 0) {
    return null;
  }
  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }
  const parsed = new Date(value);
  return isValid(parsed) ? parsed : null;
};

const differenceInDays = (a, b) => {
  const dateA = getSafeDate(a);
  const dateB = getSafeDate(b);
  if (!dateA || !dateB) return Number.POSITIVE_INFINITY;
  const diff = Math.abs(dateA.getTime() - dateB.getTime());
  return diff / (1000 * 60 * 60 * 24);
};

const computeAmountScore = (diff) => {
  if (!Number.isFinite(diff)) return 0;
  if (diff <= AUTO_MATCH_AMOUNT_TOLERANCE_STRICT) return 1;
  if (diff <= 0.05) return 0.8;
  if (diff <= 0.1) return 0.6;
  if (diff <= AUTO_MATCH_AMOUNT_TOLERANCE_SOFT) return 0.3;
  return 0;
};

const computeDateScore = (days) => {
  if (!Number.isFinite(days)) return 0;
  if (days <= 0) return 1;
  if (days <= 1) return 0.85;
  if (days <= 2) return 0.7;
  if (days <= AUTO_MATCH_DATE_TOLERANCE_DAYS) return 0.4;
  return 0;
};

const computeDescriptionScore = (normalizedA, normalizedB) => {
  if (!normalizedA || !normalizedB) return 0;
  if (normalizedA === 'unknown' || normalizedB === 'unknown') return 0;
  if (normalizedA === normalizedB) return 1;
  if (
    normalizedA.length > 3 &&
    normalizedB.length > 3 &&
    (normalizedA.includes(normalizedB) || normalizedB.includes(normalizedA))
  ) {
    return 0.9;
  }
  const tokensA = normalizedA.split(' ').filter(Boolean);
  const tokensB = normalizedB.split(' ').filter(Boolean);
  if (tokensA.length === 0 || tokensB.length === 0) return 0;
  const setB = new Set(tokensB);
  const overlap = tokensA.reduce((count, token) => (setB.has(token) ? count + 1 : count), 0);
  return overlap / Math.max(tokensA.length, tokensB.length);
};

const computeAutoMatches = (reconItems, ledgerItems, selectedAccountSet) => {
  if (!Array.isArray(reconItems) || !Array.isArray(ledgerItems)) {
    return [];
  }

  const accountSet =
    selectedAccountSet instanceof Set
      ? selectedAccountSet
      : new Set(
          Array.isArray(selectedAccountSet)
            ? selectedAccountSet
            : Object.keys(selectedAccountSet || {})
        );

  const matches = [];
  const usedLedgerIds = new Set();

  const eligibleLedger = ledgerItems.filter((txn) => (
    !txn?.isPaid ||
    getSelectedTransferAccountIds(txn, accountSet).some((accountId) => (
      !hasReconciliationMatch(txn, accountId)
    ))
  ));

  reconItems
    .filter(
      (recon) =>
        recon &&
        !recon.reconciled &&
        !recon.pending &&
        recon.accountId &&
        accountSet.has(recon.accountId)
    )
    .sort((a, b) => {
      const dateDiff = getSafeDate(a.date) - getSafeDate(b.date);
      if (Number.isFinite(dateDiff) && dateDiff !== 0) return dateDiff;
      return Math.abs(b.amount || 0) - Math.abs(a.amount || 0);
    })
    .forEach((recon) => {
      const reconAmount = Number(recon.amount);
      if (!Number.isFinite(reconAmount)) {
        return;
      }
      const reconAbs = Math.abs(reconAmount);
      if (reconAbs === 0) {
        return;
      }
      const reconDirection = reconAmount >= 0 ? 1 : -1;
      const reconNormalized =
        recon.normalizedDescription && recon.normalizedDescription !== 'unknown'
          ? recon.normalizedDescription
          : normalizeDescription(recon.description || recon.rawDescription || '');

      let bestMatch = null;

      eligibleLedger.forEach((txn) => {
        if (!txn || usedLedgerIds.has(txn.id)) return;
        if (txn.isPaid && !canReconcilePaidTransferSide(txn, recon, accountSet)) return;
        const txnAmount = Number(txn.amount);
        if (!Number.isFinite(txnAmount)) return;
        const txnAbs = Math.abs(txnAmount);
        const amountDiff = Math.abs(txnAbs - reconAbs);
        if (amountDiff > AUTO_MATCH_AMOUNT_TOLERANCE_SOFT) return;

        const dateDiff = differenceInDays(txn.date, recon.date);
        if (dateDiff > AUTO_MATCH_DATE_TOLERANCE_DAYS) return;

        const matchesAccount =
          txn.fromAccount === recon.accountId || txn.toAccount === recon.accountId;
        if (!matchesAccount) return;

        const directionMatch =
          (reconDirection < 0 && txn.fromAccount === recon.accountId) ||
          (reconDirection >= 0 && txn.toAccount === recon.accountId);
        const accountScore = directionMatch ? 1 : 0.6;

        const txnNormalized = txn.normalizedDescription
          ? txn.normalizedDescription
          : normalizeDescription(txn.description || txn.rawDescription || '');
        const descriptionScore = computeDescriptionScore(reconNormalized, txnNormalized);
        const amountScore = computeAmountScore(amountDiff);
        const dateScore = computeDateScore(dateDiff);

        const score =
          amountScore * AMOUNT_WEIGHT +
          descriptionScore * DESCRIPTION_WEIGHT +
          dateScore * DATE_WEIGHT +
          accountScore * ACCOUNT_WEIGHT;

        if (score < AUTO_MATCH_MIN_SCORE) return;

        if (
          !bestMatch ||
          score > bestMatch.score ||
          (score === bestMatch.score && amountDiff < bestMatch.amountDiff)
        ) {
          bestMatch = {
            recon,
            transaction: txn,
            score,
            confidence: Math.min(1, score / MAX_TOTAL_SCORE),
            amountDiff,
            dateDiff,
            amountScore,
            descriptionScore,
            dateScore,
            accountScore,
          };
        }
      });

  if (bestMatch) {
    matches.push(bestMatch);
    usedLedgerIds.add(bestMatch.transaction.id);
  }
});

return matches.sort((a, b) => {
  if (a.score !== b.score) return b.score - a.score;
  const dateA = getSafeDate(a.recon.date);
  const dateB = getSafeDate(b.recon.date);
  if (dateA && dateB) return dateA.getTime() - dateB.getTime();
  if (dateA) return -1;
  if (dateB) return 1;
  return 0;
});
};

const ReconciliationWidget = ({ accounts = {}, style }) => {
  const userId = auth.currentUser?.uid;
  const [selectedAccounts, setSelectedAccounts] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [ledgerTransactions, setLedgerTransactions] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tempSelection, setTempSelection] = useState({});
  const [accountForImport, setAccountForImport] = useState(null);
  const [csvInput, setCsvInput] = useState('');
  const [importSummary, setImportSummary] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isImportMenuOpen, setIsImportMenuOpen] = useState(false);
  const [showReconciled, setShowReconciled] = useState(false);
  const [autoMatchLoading, setAutoMatchLoading] = useState(false);
  const [isAutoMatchModalOpen, setIsAutoMatchModalOpen] = useState(false);
  const [autoMatchResults, setAutoMatchResults] = useState([]);
  const [autoMatchSelection, setAutoMatchSelection] = useState({});
  const [autoMatchError, setAutoMatchError] = useState(null);
  const [applyingAutoMatches, setApplyingAutoMatches] = useState(false);
  const importDropdownRef = useRef(null);

  useEffect(() => {
    if (!userId) return undefined;
    const settingsRef = ref(database, `users/${userId}/reconciliation/settings`);
    return onValue(settingsRef, (snapshot) => {
      const data = snapshot.val();
      setSelectedAccounts(data?.selectedAccounts || {});
    });
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;
    const transactionsRef = ref(database, `users/${userId}/reconciliation/transactions`);
    return onValue(transactionsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const list = Object.entries(data).map(([id, item]) => ({ id, ...item }));
      setTransactions(list);
    });
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;
    const ledgerRef = ref(database, `users/${userId}/transactions`);
    return onValue(ledgerRef, (snapshot) => {
      const data = snapshot.val() || {};
      const all = [];
      Object.entries(data).forEach(([parentId, value]) => {
        if (!value) return;
        if (value.childTransactions) {
          Object.entries(value.childTransactions).forEach(([childId, childValue]) => {
            if (!childValue) return;
            all.push({
              id: childId,
              parentId,
              ...childValue,
              normalizedDescription: normalizeDescription(childValue.description || childValue.rawDescription || ''),
            });
          });
        } else {
          all.push({
            id: parentId,
            parentId: null,
            ...value,
            normalizedDescription: normalizeDescription(value.description || value.rawDescription || ''),
          });
        }
      });
      all.sort((a, b) => {
        const dateA = getSafeDate(a.date);
        const dateB = getSafeDate(b.date);
        if (dateA && dateB) return dateA.getTime() - dateB.getTime();
        if (dateA) return -1;
        if (dateB) return 1;
        return (a.description || '').localeCompare(b.description || '', undefined, { sensitivity: 'base' });
      });
      setLedgerTransactions(all);
    });
  }, [userId]);

  const eligibleAccounts = useMemo(() => {
    return Object.entries(accounts)
      .filter(([, account]) => account && !account.archived && ALLOWED_ACCOUNT_TYPES.has(account.type))
      .sort(([, a], [, b]) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
  }, [accounts]);

  useEffect(() => {
    if (settingsOpen) {
      const sanitized = eligibleAccounts.reduce((acc, [id]) => {
        acc[id] = Boolean(selectedAccounts[id]);
        return acc;
      }, {});
      setTempSelection(sanitized);
    }
  }, [settingsOpen, selectedAccounts, eligibleAccounts]);

  useEffect(() => {
    if (!settingsOpen) {
      setIsImportModalOpen(false);
    }
  }, [settingsOpen]);

  useEffect(() => {
    if (!isImportModalOpen) {
      setAccountForImport(null);
      setCsvInput('');
      setImportSummary(null);
      setImportErrors([]);
      setImporting(false);
      setIsDragActive(false);
    }
  }, [isImportModalOpen]);

  useEffect(() => {
    if (!isImportMenuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setIsImportMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isImportMenuOpen]);

  const selectedAccountIds = useMemo(
    () => eligibleAccounts
      .filter(([id]) => Boolean(selectedAccounts[id]))
      .map(([id]) => id),
    [eligibleAccounts, selectedAccounts],
  );

  const importableAccounts = useMemo(
    () => eligibleAccounts.filter(([id]) => Boolean(selectedAccounts[id])),
    [eligibleAccounts, selectedAccounts],
  );

  const selectedAccountSet = useMemo(() => new Set(selectedAccountIds), [selectedAccountIds]);

  const filteredTransactions = useMemo(() => {
    return transactions
      .filter((txn) => {
        if (!selectedAccountSet.has(txn.accountId)) {
          return false;
        }
        if (!showReconciled && txn.reconciled) {
          return false;
        }
        return true;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [transactions, selectedAccountSet, showReconciled]);

  const selectedAutoMatchCount = useMemo(() => {
    if (!autoMatchResults.length) return 0;
    return autoMatchResults.reduce((count, match) => {
      return autoMatchSelection[match.recon.id] ? count + 1 : count;
    }, 0);
  }, [autoMatchResults, autoMatchSelection]);

  const hasEligibleLedgerTransactions = useMemo(
    () => ledgerTransactions.some((txn) => (
      !txn?.isPaid || isTransferAwaitingReconciliation(txn, selectedAccountSet)
    )),
    [ledgerTransactions, selectedAccountSet],
  );

  const handleDragStart = useCallback((event, recon, accountName) => {
    if (!event?.dataTransfer) return;
    const payload = {
      type: 'reconciliation-transaction',
      id: recon.id,
      amount: recon.amount,
      accountId: recon.accountId,
      accountName,
      date: recon.date,
      description: recon.description || recon.rawDescription || '',
      rawDescription: recon.rawDescription || '',
      pending: Boolean(recon.pending),
    };
    event.dataTransfer.effectAllowed = 'copy';
    try {
      event.dataTransfer.setData('application/x-reconciliation-transaction', JSON.stringify(payload));
    } catch (error) {
      console.warn('Failed to attach reconciliation payload', error);
    }
    event.dataTransfer.setData('text/plain', `reconciliation:${recon.id}`);
  }, []);

  const toggleAccountSelection = useCallback((accountId) => {
    setTempSelection((prev) => ({
      ...prev,
      [accountId]: !prev[accountId],
    }));
  }, []);

  const handleSaveSettings = useCallback(async () => {
    if (!userId) return;
    try {
      const cleaned = Object.entries(tempSelection)
        .filter(([id, enabled]) => enabled && accounts[id] && !accounts[id].archived && ALLOWED_ACCOUNT_TYPES.has(accounts[id].type))
        .reduce((acc, [id]) => {
          acc[id] = true;
          return acc;
        }, {});
      await saveReconciliationSettings(userId, cleaned);
      setSettingsOpen(false);
    } catch (error) {
      console.error('Failed to save reconciliation settings', error);
    }
  }, [accounts, tempSelection, userId]);

  const handleOpenImport = (accountId) => {
    setIsImportMenuOpen(false);
    setAccountForImport(accountId);
    setCsvInput('');
    setImportSummary(null);
    setImportErrors([]);
    setImporting(false);
    setIsImportModalOpen(true);
  };

  const handleCloseImport = () => {
    setIsImportModalOpen(false);
  };

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isDragActive) {
      setIsDragActive(true);
    }
  }, [isDragActive]);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    setIsDragActive(false);
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
    const { files } = event.dataTransfer || {};
    if (!files || files.length === 0) {
      return;
    }
    const file = files[0];
    if (!file) return;

    const lowerName = file.name.toLowerCase();
    const supportedText = file.type.includes('text') || lowerName.endsWith('.csv');
    const supportedExcel = lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls');

    if (supportedExcel) {
      const reader = new FileReader();
      reader.onload = (loadEvent) => {
        try {
          const data = new Uint8Array(loadEvent.target.result);
          import('xlsx').then((xlsxModule) => {
            const XLSX = xlsxModule.default || xlsxModule;
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            if (!firstSheetName) {
              setImportErrors(['No sheets found in spreadsheet.']);
              return;
            }
            const worksheet = workbook.Sheets[firstSheetName];
            const csvText = XLSX.utils.sheet_to_csv(worksheet);
            setCsvInput(csvText);
            setImportSummary(null);
            setImportErrors([]);
          }).catch((error) => {
            setImportErrors([`Failed to parse spreadsheet: ${error.message || error}`]);
          });
        } catch (parseError) {
          setImportErrors([`Failed to parse spreadsheet: ${parseError.message || parseError}`]);
        }
      };
      reader.onerror = () => {
        setImportErrors(['Failed to read spreadsheet file.']);
      };
      reader.readAsArrayBuffer(file);
      return;
    }

    if (!supportedText) {
      setImportErrors([`Unsupported file type: ${file.type || file.name}`]);
      return;
    }

    file.text().then((text) => {
      setCsvInput(text);
      setImportSummary(null);
      setImportErrors([]);
    }).catch((error) => {
      setImportErrors([`Failed to read file: ${error.message || error}`]);
    });
  }, []);

  const handleImport = async () => {
    if (!userId || !accountForImport) {
      return;
    }
    const account = accounts[accountForImport];
    if (!account) {
      setImportErrors(['Selected account no longer exists.']);
      return;
    }

    setImporting(true);
    setImportSummary(null);
    setImportErrors([]);

    try {
      const result = Papa.parse(csvInput, csvConfig);
      const rowObjects = buildRowObjects(result.data || []);
      const { parsed, failures } = parseRows(rowObjects);

      if (parsed.length === 0) {
        setImportErrors([
          failures.length
            ? 'No valid rows found. Check that the CSV includes date, description, and amount columns.'
            : 'No rows detected. Paste the raw CSV data before importing.',
        ]);
        return;
      }

      if (result.errors?.length) {
        setImportErrors(result.errors.map((err) => `Row ${err.row}: ${err.message}`));
      } else if (failures.length) {
        setImportErrors([`Skipped ${failures.length} row(s) because date or amount could not be read.`]);
      }

      const payload = parsed.map((item) => ({
        ...item,
        accountId: accountForImport,
        accountName: account.name,
        importedAt: Date.now(),
      }));

      const summary = await upsertReconciliationTransactions(userId, payload);
      setImportSummary(summary);
      setCsvInput('');
    } catch (error) {
      setImportErrors([error.message || 'Failed to import CSV data.']);
    } finally {
      setImporting(false);
    }
  };

  const handleMarkReconciled = async (transactionId, currentValue) => {
    if (!userId) return;
    try {
      await markReconciliationTransaction(userId, transactionId, !currentValue);
    } catch (error) {
      console.error('Failed to toggle reconciled state', error);
    }
  };

  const handleAutoMatch = useCallback(() => {
    if (autoMatchLoading) return;
    setAutoMatchLoading(true);
    setAutoMatchError(null);
    try {
      if (!(selectedAccountSet instanceof Set) || selectedAccountSet.size === 0) {
        setAutoMatchResults([]);
        setAutoMatchSelection({});
        setAutoMatchError('Enable reconciliation accounts in settings first.');
        setIsAutoMatchModalOpen(true);
        return;
      }

      const matches = computeAutoMatches(transactions, ledgerTransactions, selectedAccountSet);
      setAutoMatchResults(matches);
      const initialSelection = matches.reduce((acc, item) => {
        acc[item.recon.id] = true;
        return acc;
      }, {});
      setAutoMatchSelection(initialSelection);
      if (matches.length === 0) {
        setAutoMatchError('No confident matches found. You may need to reconcile manually.');
      }
      setIsAutoMatchModalOpen(true);
    } catch (error) {
      console.error('Failed to compute auto matches', error);
      setAutoMatchResults([]);
      setAutoMatchSelection({});
      setAutoMatchError('Failed to compute matches. Please try again.');
      setIsAutoMatchModalOpen(true);
    } finally {
      setAutoMatchLoading(false);
    }
  }, [autoMatchLoading, transactions, ledgerTransactions, selectedAccountSet]);

  const toggleAutoMatchSelection = useCallback((reconId) => {
    setAutoMatchSelection((prev) => ({
      ...prev,
      [reconId]: !prev[reconId],
    }));
  }, []);

  const handleCloseAutoMatchModal = useCallback(() => {
    if (applyingAutoMatches) return;
    setIsAutoMatchModalOpen(false);
    setAutoMatchResults([]);
    setAutoMatchSelection({});
    setAutoMatchError(null);
  }, [applyingAutoMatches]);

  const handleApplyAutoMatches = useCallback(async () => {
    if (!userId) return;
    const selected = autoMatchResults.filter((match) => autoMatchSelection[match.recon.id]);
    if (selected.length === 0) {
      setIsAutoMatchModalOpen(false);
      setAutoMatchResults([]);
      setAutoMatchSelection({});
      setAutoMatchError(null);
      return;
    }
    setApplyingAutoMatches(true);
    setAutoMatchError(null);
    try {
      for (const match of selected) {
        const { recon, transaction } = match;
        if (!transaction || !recon || recon.reconciled) {
          continue;
        }
        const rawAmount = Number.isFinite(Number(transaction.amount))
          ? Number(transaction.amount)
          : Number(recon.amount);
        const absAmount = Math.abs(rawAmount || 0);
        if (absAmount === 0) {
          continue;
        }
        if (!transaction.isPaid) {
          await markTransactionPaid(
            userId,
            transaction.id,
            transaction.parentId || null,
            transaction.fromAccount || null,
            transaction.toAccount || null,
            absAmount,
          );
        }
        if (recon.accountId) {
          await markTransactionReconciliationMatch(
            userId,
            transaction.id,
            transaction.parentId || null,
            recon.accountId,
            recon.id,
            true,
          );
        }
        await markReconciliationTransaction(userId, recon.id, true);
      }
      setIsAutoMatchModalOpen(false);
      setAutoMatchResults([]);
      setAutoMatchSelection({});
    } catch (error) {
      console.error('Failed to apply auto matches', error);
      setAutoMatchError('Failed to apply selected matches. Please try again.');
    } finally {
      setApplyingAutoMatches(false);
    }
  }, [userId, autoMatchResults, autoMatchSelection]);

  return (
    <Card style={style}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
        <h2 style={{ marginRight: 'auto' }}>Reconciliation</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
          <Button
            type="button"
            onClick={handleAutoMatch}
            style={{ whiteSpace: 'nowrap', minWidth: '112px', justifyContent: 'center' }}
            disabled={
              autoMatchLoading ||
              applyingAutoMatches ||
              selectedAccountIds.length === 0 ||
              !hasEligibleLedgerTransactions
            }
            title={
              selectedAccountIds.length === 0
                ? 'Enable reconciliation accounts in settings first'
                : !hasEligibleLedgerTransactions
                  ? 'No unpaid transactions available to match right now'
                  : 'Automatically match reconciliation items to unpaid transactions'
            }
          >
            {autoMatchLoading ? 'Matching…' : 'Auto Match'}
          </Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9em' }}>
            <span>Show reconciled</span>
            <ToggleWrapper title="Toggle showing reconciled items">
              <ToggleInput
                type="checkbox"
                checked={showReconciled}
                onChange={() => setShowReconciled((prev) => !prev)}
              />
              <ToggleSlider />
            </ToggleWrapper>
          </div>
          <Dropdown ref={importDropdownRef}>
            <Button
              type="button"
              onClick={() => setIsImportMenuOpen((prev) => !prev)}
              disabled={importableAccounts.length === 0}
              title={
                importableAccounts.length === 0
                  ? 'Enable reconciliation accounts in settings first'
                  : 'Import transactions for an enabled account'
              }
            >
              Import
            </Button>
            {isImportMenuOpen && importableAccounts.length > 0 && (
              <DropdownContent>
                {importableAccounts.map(([id, account]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => handleOpenImport(id)}
                  >
                    {account.name}
                  </button>
                ))}
              </DropdownContent>
            )}
          </Dropdown>
          <Button onClick={() => setSettingsOpen(true)}>Settings</Button>
        </div>
      </div>
      <CardContent style={{ minHeight: 0 }}>
        {selectedAccountIds.length === 0 ? (
          <p>Select one or more accounts in settings to begin reconciling imported transactions.</p>
        ) : (
          <TableContainer
            style={{
              height: 'auto',
              maxHeight: '260px',
              padding: 0,
              marginTop: '14px',
              boxSizing: 'border-box',
              scrollbarGutter: 'stable',
              flex: '1 1 auto',
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <Table style={{ tableLayout: 'fixed', minWidth: 0, margin: 0 }}>
              <colgroup>
                <col style={{ width: '16%' }} />
                <col style={{ width: '31%' }} />
                <col style={{ width: '24%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '14%' }} />
              </colgroup>
              <Thead>
                <Tr>
                  <Th style={compactCellStyle}>Date</Th>
                  <Th style={compactCellStyle}>Description</Th>
                  <Th style={compactCellStyle}>Account</Th>
                  <Th style={compactCellStyle}>Amount</Th>
                  <Th style={compactCenteredCellStyle}>Reconciled</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredTransactions.length === 0 ? (
                  <Tr>
                    <Td colSpan={5} style={compactCellStyle}>
                      {showReconciled ? 'No reconciliation records found.' : 'No unreconciled transactions right now.'}
                    </Td>
                  </Tr>
                ) : (
                  filteredTransactions.map((txn) => {
                    const fullDescription = txn.description || txn.rawDescription || '';
                    const displayDescription = fullDescription.length > 20
                      ? `${fullDescription.slice(0, 20)}…`
                      : fullDescription;
                    const accountLabel = accounts[txn.accountId]?.name || txn.accountName || 'Unknown';
                    return (
                      <Tr
                        key={txn.id}
                        draggable
                        onDragStart={(event) => handleDragStart(event, txn, accountLabel)}
                        style={{ cursor: 'grab' }}
                      >
                        <Td style={compactCellStyle}>{formatDisplayDate(txn.date)}</Td>
                        <Td style={compactCellStyle} title={fullDescription}>{displayDescription}</Td>
                        <Td style={compactCellStyle} title={accountLabel}>{accountLabel}</Td>
                        <Td style={compactCellStyle}>{formatCurrency(txn.amount)}</Td>
                        <Td style={compactCenteredCellStyle}>
                          <input
                            type="checkbox"
                            checked={Boolean(txn.reconciled)}
                            onChange={() => handleMarkReconciled(txn.id, txn.reconciled)}
                            aria-label="Mark as reconciled"
                          />
                        </Td>
                      </Tr>
                    );
                  })
                )}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {isAutoMatchModalOpen && (
        <ModalContainer onClick={handleCloseAutoMatchModal}>
          <ModalContent
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '960px', width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            <h3>Auto Match Results</h3>
            <p>Review the proposed matches between imported reconciliation items and unpaid transactions.</p>
            {autoMatchError && (
              <p style={{ color: '#c26166', margin: 0 }}>{autoMatchError}</p>
            )}
            {autoMatchResults.length > 0 ? (
              <>
                <TableContainer style={{ maxHeight: '360px', overflowY: 'auto' }}>
                  <Table>
                    <Thead>
                      <Tr>
                        <Th style={{ width: '56px' }}>Select</Th>
                        <Th>Reconciliation Item</Th>
                        <Th>Unpaid Transaction</Th>
                        <Th style={{ width: '140px' }}>Confidence</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {autoMatchResults.map((match) => {
                        const { recon, transaction } = match;
                        const txn = transaction || {};
                        const reconAccount = accounts[recon.accountId]?.name || recon.accountName || 'Unknown';
                        const ledgerFrom = txn.fromAccount
                          ? accounts[txn.fromAccount]?.name || txn.fromAccount
                          : '';
                        const ledgerTo = txn.toAccount
                          ? accounts[txn.toAccount]?.name || txn.toAccount
                          : '';
                        const ledgerAccountLabel =
                          ledgerFrom && ledgerTo && ledgerFrom !== ledgerTo
                            ? `${ledgerFrom} → ${ledgerTo}`
                            : ledgerFrom || ledgerTo || reconAccount;
                        const confidencePercent = Math.round((match.confidence || 0) * 100);
                        const scoreDisplay = Number.isFinite(match.score) ? match.score.toFixed(1) : '0.0';
                        const txnDescription = txn.description || txn.rawDescription || '—';
                        return (
                          <Tr key={`${recon.id}-${txn.id}`}>
                            <Td style={{ textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={Boolean(autoMatchSelection[recon.id])}
                                onChange={() => toggleAutoMatchSelection(recon.id)}
                                disabled={applyingAutoMatches}
                                aria-label={`Select auto match for ${recon.description || recon.rawDescription || 'reconciliation item'}`}
                              />
                            </Td>
                            <Td>
                              <div style={{ fontWeight: 600 }}>
                                {formatDisplayDate(recon.date)} · {formatCurrency(recon.amount)}
                              </div>
                              <div>{recon.description || recon.rawDescription || '—'}</div>
                              <div style={{ color: '#666', fontSize: '0.85em' }}>Account: {reconAccount}</div>
                              {Number.isFinite(match.dateDiff) && (
                                <div style={{ color: '#666', fontSize: '0.75em' }}>
                                  Δ days: {Math.round(match.dateDiff ?? 0)}
                                </div>
                              )}
                            </Td>
                            <Td>
                              <div style={{ fontWeight: 600 }}>
                                {formatDisplayDate(txn.date)} · {formatCurrency(txn.amount)}
                              </div>
                              <div>{txnDescription}</div>
                              <div style={{ color: '#666', fontSize: '0.85em' }}>{ledgerAccountLabel}</div>
                              <div style={{ color: '#666', fontSize: '0.75em' }}>
                                Δ amount: {formatCurrency(match.amountDiff || 0)}
                              </div>
                            </Td>
                            <Td>
                              <div style={{ fontWeight: 600 }}>{confidencePercent}%</div>
                              <div style={{ color: '#666', fontSize: '0.75em' }}>Score {scoreDisplay}</div>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
                <div style={{ fontSize: '0.9em', color: '#666' }}>
                  Selected matches: {selectedAutoMatchCount} of {autoMatchResults.length}
                </div>
              </>
            ) : (
              <p>No matches were identified. You may need to reconcile items manually.</p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button type="button" onClick={handleCloseAutoMatchModal} disabled={applyingAutoMatches}>
                Close
              </Button>
              {autoMatchResults.length > 0 && (
                <Button
                  type="button"
                  onClick={handleApplyAutoMatches}
                  disabled={applyingAutoMatches || selectedAutoMatchCount === 0}
                >
                  {applyingAutoMatches ? 'Applying…' : `Accept Selected (${selectedAutoMatchCount})`}
                </Button>
              )}
            </div>
          </ModalContent>
        </ModalContainer>
      )}

      {settingsOpen && (
        <ModalContainer onClick={() => setSettingsOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h3>Reconciliation Accounts</h3>
            <p>Select which accounts participate in reconciliation.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              {eligibleAccounts.length === 0 && <p>No bank or loan accounts available.</p>}
              {eligibleAccounts.map(([id, account]) => (
                <div key={id} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label style={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={Boolean(tempSelection[id])}
                      onChange={() => toggleAccountSelection(id)}
                    />
                    <span>{account.name}</span>
                    <span style={{ color: '#888' }}>({account.type})</span>
                  </label>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button type="button" onClick={() => setSettingsOpen(false)}>Cancel</Button>
              <Button type="button" onClick={handleSaveSettings}>Save</Button>
            </div>
          </ModalContent>
        </ModalContainer>
      )}

      {isImportModalOpen && accountForImport && (
        <ModalContainer onClick={handleCloseImport}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h3>Import Transactions</h3>
            <p>Account: {accounts[accountForImport]?.name || 'Selected Account'}</p>
            <p>Paste the raw CSV content below or drag and drop a CSV/XLS/XLSX file. Columns for date, description, and amount will be detected automatically.</p>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              style={{
                border: isDragActive ? '2px dashed #4CAF50' : '1px solid transparent',
                borderRadius: '8px',
                transition: 'border 0.2s ease-in-out',
              }}
            >
              <Textarea
                value={csvInput}
                onChange={(e) => setCsvInput(e.target.value)}
                placeholder="Date,Description,Amount\n2024-05-01,Coffee Shop,-5.75"
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginTop: '12px' }}>
              <Button type="button" onClick={handleImport} disabled={importing || !csvInput.trim()}>
                {importing ? 'Importing…' : 'Import'}
              </Button>
              {importSummary && (
                <span>
                  Added {importSummary.added.length} · Updated {importSummary.updated.length} · Skipped {importSummary.skipped.length}
                </span>
              )}
            </div>
            {importErrors.length > 0 && (
              <ul style={{ marginTop: '8px', paddingLeft: '18px' }}>
                {importErrors.map((err, idx) => (
                  <li key={idx} style={{ color: '#c26166' }}>{err}</li>
                ))}
              </ul>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <Button type="button" onClick={handleCloseImport}>Close</Button>
            </div>
          </ModalContent>
        </ModalContainer>
      )}
    </Card>
  );
};

export default ReconciliationWidget;
